import React, { useEffect, useState } from "react";
import {
  fetchBoqByProject,
  fetchBoqItemsWithFiles,
  updateBoqItemStatus,
  viewBoqItemFilesApi
} from "../../services/api/boq/boqService";
import { fetchProjectsApi } from "../../services/api/boq/projectService";
import { toast } from "react-hot-toast";

const BoqAttachmentExplorer = () => {
  const [viewLevel, setViewLevel] = useState("projects");

  const [projects, setProjects] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [boqDetails, setBoqDetails] = useState(null);
  const [items, setItems] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedBoq, setSelectedBoq] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // FILE FILTERS
  const [filter, setFilter] = useState("ALL");
  const [fileTypeFilter, setFileTypeFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  // ITEM FILTERS
  const [itemSearch, setItemSearch] = useState("");
  const [itemStatusFilter, setItemStatusFilter] = useState("ALL");
  const [itemTypeFilter, setItemTypeFilter] = useState("ALL");

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await fetchProjectsApi();
      setProjects(res?.data?.data?.data || []);
    } catch {
      toast.error("Failed to load projects");
    }
    setLoading(false);
  };

  const loadBoqs = async (projectId) => {
    setLoading(true);
    try {
      const data = await fetchBoqByProject(projectId);
      setBoqs(data || []);
    } catch {
      toast.error("Failed to load BOQs");
    }
    setLoading(false);
  };

  const loadBoqItems = async (boqId) => {
    setLoading(true);
    try {
      const data = await fetchBoqItemsWithFiles(boqId);

      setBoqDetails(data.boq);

      const formatted = (data.items || []).map((item) => ({
        ...item,
        files: item.files || []
      }));

      setItems(formatted);
      setViewLevel("items");
    } catch {
      toast.error("Failed to load BOQ items");
    }
    setLoading(false);
  };

  const handleViewFile = async (fileId) => {
    try {
      const res = await viewBoqItemFilesApi(fileId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      window.open(url);
    } catch {
      toast.error("Failed to open file");
    }
  };

  const handleStatusChange = async (fileId, status) => {
    setActionLoadingId(fileId);
    try {
      await updateBoqItemStatus(fileId, status);

      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          files: item.files.map((f) =>
            f.id === fileId ? { ...f, approval_status: status } : f
          )
        }))
      );

      toast.success(`File ${status}`);
    } catch {
      toast.error("Update failed");
    }
    setActionLoadingId(null);
  };

  // FILE FILTER
  const getFilteredFiles = (files = []) => {
    return files.filter((f) => {
      const matchesStatus =
        filter === "ALL" ||
        f.approval_status?.toLowerCase() === filter;

      const matchesType =
        fileTypeFilter === "ALL" ||
        f.document_type === fileTypeFilter;

      const matchesSearch = search.trim()
        ? (
          f.file_name?.toLowerCase().includes(search.toLowerCase()) ||
          f.document_type?.toLowerCase().includes(search.toLowerCase())
        )
        : true;

      return matchesStatus && matchesType && matchesSearch;
    });
  };

  // ITEM FILTER
  const getFilteredItems = () => {
    return items.filter((item) => {
      const files = item.files || [];

      const matchesType =
        itemTypeFilter === "ALL"
          ? true
          : files.some((f) => f.document_type === itemTypeFilter);

      const matchesSearch = itemSearch.trim()
        ? item.item_name?.toLowerCase().includes(itemSearch.toLowerCase())
        : true;

      const matchesStatus =
        itemStatusFilter === "ALL"
          ? true
          : files.some(
            (f) =>
              f.approval_status?.toLowerCase() === itemStatusFilter
          );

      return matchesSearch && matchesStatus && matchesType;
    });
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">

      {/* HEADER */}
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">
          Projects Attachment Explorer
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          {viewLevel === "projects" && "Projects"}
          {viewLevel === "boqs" && selectedProject?.project_name}
          {viewLevel === "items" &&
            `${selectedProject?.project_name} / ${selectedBoq?.boq_name}`}
          {viewLevel === "files" &&
            `${selectedProject?.project_name} / ${selectedBoq?.boq_name} / ${selectedItem?.item_name}`}
        </p>
      </div>

      {/* FILE FILTER */}
      {viewLevel === "files" && (
        <div className="bg-white border rounded-2xl p-4 mb-4 flex flex-wrap gap-3 shadow-sm">
          <input
            type="text"
            placeholder="🔍 Search..."
            className="border border-gray-200 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="border border-gray-200 px-3 py-2 rounded-lg text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            className="border border-gray-200 px-3 py-2 rounded-lg text-sm"
            value={fileTypeFilter}
            onChange={(e) => setFileTypeFilter(e.target.value)}
          >
            <option value="ALL">All Types</option>
            <option value="MIR">MIR</option>
            <option value="TDS">TDS</option>
          </select>
        </div>
      )}

      {/* PROJECTS */}
      {viewLevel === "projects" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {projects.map((p) => (
            <div
              key={p.id}
              onClick={() => {
                setSelectedProject(p);
                loadBoqs(p.id);
                setViewLevel("boqs");
              }}
              className="cursor-pointer bg-white border p-4 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition"
            >
              <div className="text-3xl mb-2">📁</div>
              <p className="text-sm font-medium">{p.project_name}</p>
            </div>
          ))}
        </div>
      )}

      {/* BOQS */}
      {viewLevel === "boqs" && (
        <div>
          <button className="mb-3 text-blue-600 hover:underline" onClick={() => setViewLevel("projects")}>
            ← Back
          </button>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {boqs.map((b) => (
              <div
                key={b.id}
                onClick={() => {
                  setSelectedBoq(b);
                  loadBoqItems(b.id);
                }}
                className="cursor-pointer bg-white border p-4 rounded-2xl shadow-sm hover:shadow-lg"
              >
                📁 {b.boq_name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ITEMS */}
      {viewLevel === "items" && (
        <div>
          <button className="mb-3 text-blue-600 hover:underline" onClick={() => setViewLevel("boqs")}>
            ← Back
          </button>

          <div className="bg-white border rounded-2xl p-4 mb-4 flex gap-3 shadow-sm">
            <input
              placeholder="Search item..."
              className="border px-3 py-2 rounded-lg text-sm"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
            />

            <select
              className="border px-3 py-2 rounded-lg text-sm"
              value={itemStatusFilter}
              onChange={(e) => setItemStatusFilter(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              className="border border-gray-200 px-3 py-2 rounded-lg text-sm"
              value={itemTypeFilter}
              onChange={(e) => setItemTypeFilter(e.target.value)}
            >
              <option value="ALL">All Types</option>
              <option value="MIR">MIR</option>
              <option value="TDS">TDS</option>
            </select>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {getFilteredItems().map((item) => (
              <div
                key={item.id}
                onClick={() => {
                  setSelectedItem(item);
                  setViewLevel("files");
                }}
                className="cursor-pointer bg-white border p-4 rounded-2xl shadow-sm hover:shadow-lg"
              >
                📁
                <p className="text-sm font-semibold mt-2">{item.item_name}</p>
                <p className="text-xs text-gray-400">
                  {item.files.length} Files
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FILES */}
      {viewLevel === "files" && (
        <div>
          <button className="mb-3 text-blue-600 hover:underline" onClick={() => setViewLevel("items")}>
            ← Back
          </button>

          {(() => {
            const files = getFilteredFiles(selectedItem?.files);
            const pending = files.filter(f => f.approval_status === "pending").length;
            const approved = files.filter(f => f.approval_status === "approved").length;
            const rejected = files.filter(f => f.approval_status === "rejected").length;

            return (
              <>
                <div className="flex gap-2 mb-4 text-xs flex-wrap">
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Total: {files.length}</span>
                  <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full">Pending: {pending}</span>
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full">Approved: {approved}</span>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full">Rejected: {rejected}</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className="bg-white border border-gray-100 p-3 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-200"
                    >
                      {/* Icon */}
                      <div className="h-20 flex items-center justify-center bg-gray-50 rounded-xl mb-2 text-2xl">
                        📄
                      </div>

                      {/* File Name */}
                      <p className="text-xs font-medium text-gray-700 truncate">
                        {file.file_name}
                      </p>

                      {/* Type + Status */}
                      <div className="flex justify-between items-center mt-2 text-[10px]">
                        <span className="bg-gray-100 px-2 py-0.5 rounded">
                          {file.document_type}
                        </span>

                        <span
                          className={`px-2 py-0.5 rounded text-white ${file.approval_status === "approved"
                            ? "bg-green-500"
                            : file.approval_status === "rejected"
                              ? "bg-red-500"
                              : "bg-yellow-500"
                            }`}
                        >
                          {file.approval_status}
                        </span>
                      </div>

                      {/* View Button */}
                      <button
                        onClick={() => handleViewFile(file.id)}
                        className="w-full mt-2 bg-blue-500 hover:bg-blue-600 text-white text-xs py-1.5 rounded-lg"
                      >
                        View
                      </button>

                      {/* Approve / Reject */}
                      {file.approval_status === "pending" && (
                        <div className="flex gap-2 mt-2">
                          <button
                            disabled={actionLoadingId === file.id}
                            onClick={() => handleStatusChange(file.id, "approved")}
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs py-1.5 rounded-lg disabled:opacity-50"
                          >
                            {actionLoadingId === file.id ? "..." : "Approve"}
                          </button>

                          <button
                            disabled={actionLoadingId === file.id}
                            onClick={() => handleStatusChange(file.id, "rejected")}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs py-1.5 rounded-lg disabled:opacity-50"
                          >
                            {actionLoadingId === file.id ? "..." : "Reject"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {loading && <div className="text-center py-6">Loading...</div>}
    </div>
  );
};

export default BoqAttachmentExplorer;